import { useRef } from "react";
import { ArrowUpRight, ArrowDown, Phone, Sun } from "lucide-react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { COMPANY, HERO_BG } from "../lib/constants";
import { useScrollTo } from "../hooks/useScrollTo";
import { SkyBackground } from "./SkyBackground";

const Headline = ({ reduced }) => (
  <h1 className="hero-headline text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-headline">
    {['Powering homes &', 'businesses with', 'smart solar energy.'].map((line, i) => (
      <span className={`hero-line ${i === 2 ? 'solar-text' : ''}`} key={line}>
        <motion.span initial={reduced ? false : { y: '110%' }} animate={{ y: 0 }}
          transition={{ duration: 1.05, delay: 0.18 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}>{line}</motion.span>
        {i < 2 && ' '}
      </span>
    ))}
  </h1>
);

export const Hero = () => {
  const ref = useRef(null);
  const scrollTo = useScrollTo();
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 70]);
  return (
    <section ref={ref} id="hero" data-testid="hero-section" className="solar-hero">
      <SkyBackground />
      <div className="site-container hero-layout">
        <div className="hero-copy">
          <motion.p className="eyebrow hero-eyebrow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} data-testid="hero-location">
            <span className="status-dot" /> SOLAR ENERGY SOLUTIONS / KERALA
          </motion.p>
          <Headline reduced={reduced} />
          <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: reduced ? 0 : 0.7, duration: 0.7 }}>
            <p className="hero-description" data-testid="hero-subheading">Your rooftop. A brighter tomorrow. Reliable solar solutions for homes, businesses, industries and farms across Kerala.</p>
            <div className="hero-actions">
              <motion.button onClick={() => scrollTo('solutions')} data-testid="hero-cta-services" className="solar-button" whileHover={{ y: -3 }} whileTap={{ scale: 0.97 }}>Explore Our Services <ArrowUpRight size={19} /></motion.button>
              <motion.a href={`tel:${COMPANY.phones[0].replace(/\s/g, '')}`} data-testid="hero-cta-call" className="hero-call" whileHover={{ x: 3 }}><Phone size={16} /> Call Now</motion.a>
            </div>
          </motion.div>
          <div className="hero-footnote" data-testid="hero-tagline"><Sun size={20} /><span>Own The Best!</span><span className="hero-footnote-line" /><span className="eyebrow">DESIGNED AROUND YOU</span></div>
        </div>
        <motion.div className="hero-visual" initial={reduced ? false : { opacity: 0, clipPath: 'inset(0 0 100% 0)' }} animate={{ opacity: 1, clipPath: 'inset(0 0 0% 0)' }} transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}>
          <div className="hero-photo-frame">
            <motion.img src={HERO_BG} alt="Solar panels capturing sunlight for clean renewable energy" className="hero-photo" style={{ y: reduced ? 0 : y }} data-testid="hero-solar-photo" />
            <div className="photo-crosshair" aria-hidden="true">+</div>
            <div className="hero-photo-caption" data-testid="hero-photo-caption"><span className="eyebrow">CLEAN ENERGY. REAL POSSIBILITIES.</span><ArrowUpRight size={28} /></div>
          </div>
          <div className="hero-brand-stamp"><img src={COMPANY.logo} alt="TECH THRISSUR — Own The Best!" data-testid="hero-logo" width="588" height="642" /></div>
          <span className="hero-image-index eyebrow" aria-hidden="true">SUNLIGHT → POSSIBILITY</span>
        </motion.div>
      </div>
      <div className="site-container hero-bottom"><span className="eyebrow">ON-GRID / OFF-GRID / HYBRID</span><button onClick={() => scrollTo('about')} data-testid="hero-scroll-explore" className="hero-scroll eyebrow">SCROLL TO DISCOVER <ArrowDown size={16} /></button></div>
    </section>
  );
};
